/**
 * Chart trade lines (SPEC §9.3; owner mockup 01 "▲ LONG 1 −$12.50 ×", "TP ~ +$737.50 −1 ×"; owner
 * request 2026-09-28 "enable chart trading like Tradesea / Tradovate"). Built on the main thread
 * from the trading store, posted to the chart engine (`TradeLine`, structurally identical in
 * @helm/chart), which draws them, hit-tests the chips and reports drags / × clicks back.
 *
 * - **Which orders:** only orders still live at the exchange (`isWorkingOrder`: pending submit,
 *   working, partially filled, pending cancel / modify, triggered, unknown). Filled, cancelled,
 *   rejected and not-placed orders never make a line; the host filters with the same predicate the
 *   Orders table's "Open" filter uses, and a finished order disappears on the frame that finished it.
 * - **Position line** at the exact average (Σ price×qty / |q|, may be fractional ticks), qty signed,
 *   open P&L at the mark.
 * - **Order lines** at the limit (LIMIT, MIT) or stop (STOP, STOP-LIMIT, trailing) price. A
 *   reducing order carries the P&L it would realise if it filled: open P&L at that price × order
 *   qty / |q|, rounded half away from zero to the cent — "~" because a partial exit of a scaled-in
 *   position is priced at the average.
 * - **P&L unit** (setting `chart_pnl_unit`): cash (default, integer cents), points or ticks (the
 *   per-contract distance from the average entry, in the trade's favour).
 * - **Controls:** a line is draggable (one ModifyOrder on release) only when its order is WORKING /
 *   PARTIALLY_FILLED, is a limit / stop / stop-limit (never trailing or MIT: a 314 would drop their
 *   trailing / touch semantics) and modifies are allowed; the × (CancelOrder) only when the order
 *   is WORKING / PARTIALLY_FILLED and cancels are allowed. A pending state is shown on the line
 *   ("Cancelling…") instead of offering an action the gateway would refuse.
 */
import { openPnlCents, openTickContracts, roundHalfAway, type ExactPosition } from "@helm/core/trading";
import { MINUS, PRIVACY_MASK, formatCents } from "./format.ts";
import { protectSetOf, type ProtectSet } from "./chartProtect.ts";

/**
 * Proto enum values (helm.v1 common.proto). Plain numbers: the generated TS enums cannot run under
 * the node test runner's type stripping, and the wire values never change.
 */
export const PB = {
  SIDE_SELL: 2,
  LIMIT: 2,
  STOP_MARKET: 3,
  STOP_LIMIT: 4,
  TRAILING_STOP: 5,
  MIT: 6,
  ROLE_TARGET: 2,
  ROLE_STOP: 3,
  // OrderState
  PENDING_SUBMIT: 2,
  WORKING: 3,
  PARTIALLY_FILLED: 4,
  PENDING_CANCEL: 6,
  PENDING_MODIFY: 8,
  TRIGGERED: 10,
  UNKNOWN: 11,
} as const;

/** How line P&L is shown (synced setting `chart_pnl_unit`). */
export type ChartPnlUnit = "cash" | "points" | "ticks";
export const CHART_PNL_UNITS: readonly ChartPnlUnit[] = ["cash", "points", "ticks"];

/** Instrument tick facts for points / ticks labels. */
export type LineTick = { tickNum: number; tickDen: number; decimals: number };

/**
 * What a line's P&L-if-filled depends on, so the chart can relabel its ghost while it is dragged
 * (the engine passes it back to `linePnlAt`; it never reads it). JSON-safe (bigint as string).
 */
export type LinePnlBasis = {
  netQty: number;
  openCostTicks: string;
  /** Contracts this order closes (min(open, |net|)). */
  closeQty: number;
  tickValueCents: number;
  unit: ChartPnlUnit;
  tick: LineTick | null;
  privacy: boolean;
};

export type ChartTradeLine = {
  id: string;
  /** Opaque host ownership; never a price or an order-routing input. */
  contextKey?: string;
  kind: "position" | "target" | "stop" | "order";
  priceTicks: number;
  label: string;
  qty: string;
  pnl: string | null;
  pnlTone: "pos" | "neg" | "zero" | "none";
  side: "buy" | "sell";
  /** Hover text of the chips. */
  tooltip: string;
  /** The order this line is (null: the position line). */
  orderId: string | null;
  accountId: string;
  /** Stops and bracket legs dashed, limits and the position solid (SPEC §9.3). */
  dashed: boolean;
  /** Drag the label to move the order (one ModifyOrder on release). */
  draggable: boolean;
  /** The × cancels the order. */
  cancellable: boolean;
  /** A pending state shown on the line instead of the × ("Cancelling…"); null when none. */
  status: string | null;
  /**
   * P&L-if-filled inputs for the drag ghost; null when the order does not reduce a position. A
   * position line's is the whole position (the TP / SL drag preview).
   */
  pnlBasis: LinePnlBasis | null;
  /**
   * Position lines: which protective orders a drag off the tag may still create (Tradesea: the
   * profit direction a TP, the loss direction an SL; `chartProtect.ts`); null: the tag does not
   * drag. Always null on order lines.
   */
  protect: ProtectSet | null;
  /** Position protection's own explanation; never extracted from close / reverse tooltip text. */
  protectReason: string | null;
  /**
   * Position lines (Tradesea tag): the ⇅ reverses the position (Reverse & Cxl) / the × closes it
   * (Exit at Mkt & Cxl) — only when that exit's gate allows it. Always false on order lines.
   */
  reversible: boolean;
  closable: boolean;
  /** Position lines with several accounts shown: the account on the tag; null otherwise. */
  account: string | null;
};

/**
 * Why the chips do nothing (the gate's reason), or null when trading is allowed. `protect`: why a
 * TP / SL may not be dragged off the account's position line (absent: as `modify`). `close` /
 * `reverse`: why the position tag's × / ⇅ is not offered (absent: not offered).
 */
export type LineGates = {
  modify: string | null;
  cancel: string | null;
  protect?: string | null;
  close?: string | null;
  reverse?: string | null;
};

export type LinePosition = {
  contextKey?: string;
  accountId: string;
  position: ExactPosition;
  /** Open P&L at the mark, cents; null when unknown. */
  unrealizedCents: bigint | null;
  /** The mark (ticks) the open P&L is taken at; null when unknown. */
  markTicks?: number | null;
};

export type LineOrder = {
  contextKey?: string;
  clientOrderId: string;
  accountId: string;
  side: number;
  type: number;
  qty: number;
  filled: number;
  limitTicks: bigint;
  stopTicks: bigint;
  bracketRole: number;
  /** Proto `OrderState`; omitted = WORKING. */
  state?: number;
  /** The bracket a child belongs to ("PB-…": the position's own bracket; else an entry's). */
  parentId?: string;
};

const signedQty = (q: number): string => (q < 0 ? `${MINUS}${String(-q)}` : String(q));

function tone(c: bigint | null): ChartTradeLine["pnlTone"] {
  return c === null ? "none" : c > 0n ? "pos" : c < 0n ? "neg" : "zero";
}

function orderPrice(o: LineOrder): bigint | null {
  switch (o.type) {
    case PB.LIMIT:
    case PB.MIT:
      return o.limitTicks;
    case PB.STOP_MARKET:
    case PB.STOP_LIMIT:
    case PB.TRAILING_STOP:
      return o.stopTicks;
    default:
      return null; // market orders have no line
  }
}

const TYPE_SHORT: Record<number, string> = {
  [PB.LIMIT]: "LMT",
  [PB.MIT]: "MIT",
  [PB.STOP_MARKET]: "STP",
  [PB.STOP_LIMIT]: "STL",
  [PB.TRAILING_STOP]: "TRL",
};

const STATUS: Record<number, string> = {
  [PB.PENDING_SUBMIT]: "Pending…",
  [PB.PENDING_CANCEL]: "Cancelling…",
  [PB.PENDING_MODIFY]: "Modifying…",
  [PB.TRIGGERED]: "Triggered",
  [PB.UNKNOWN]: "Unknown",
};

/**
 * Why a line in `state` cannot be dragged or cancelled now (ADR-0059: shown when a drag is tried,
 * never a silent no-op). A pending cancel / modify waits for Rithmic's answer; one that never comes
 * remains possibly live until a broker response settles it. Resync is not available while a
 * managed bracket is active, and omission from a response does not settle an order.
 */
export function notActionableWhy(state: number, status: string | null): string {
  if (state === PB.PENDING_MODIFY || state === PB.PENDING_CANCEL)
    return `${status ?? "Pending"}: Rithmic has not confirmed the last ${
      state === PB.PENDING_MODIFY ? "change" : "cancel"
    } yet. This order remains possibly live; check R|Trader. Resync applies broker-confirmed updates only and is refused while a managed bracket is active`;
  if (state === PB.UNKNOWN)
    return "Unknown: Rithmic has not confirmed this order — check R|Trader; it can be dragged once confirmed";
  return `${status ?? "Not working"}: wait for the exchange`;
}

/** States a line can be moved or cancelled in (never while another operation is in flight). */
const ACTIONABLE = new Set<number>([PB.WORKING, PB.PARTIALLY_FILLED]);
/** Types a drag may move (a 314 on a trailing stop or MIT would lose its semantics). */
const DRAGGABLE_TYPES = new Set<number>([PB.LIMIT, PB.STOP_MARKET, PB.STOP_LIMIT]);

// -------------------------------------------------------------------------------------- P&L

/** `num / den` as a decimal string with `dec` places, halves away from zero; ASCII digits. */
function ratioText(num: bigint, den: bigint, dec: number): string {
  const scale = 10n ** BigInt(dec);
  const q = roundHalfAway(num * scale, den);
  const neg = q < 0n;
  const m = neg ? -q : q;
  const int = m / scale;
  const frac = m % scale;
  const body = dec > 0 ? `${int.toString()}.${frac.toString().padStart(dec, "0")}` : int.toString();
  return q === 0n ? body : `${neg ? MINUS : "+"}${body}`;
}

/**
 * Per-contract distance (ticks) from the average to `priceTicks` in the trade's favour, as
 * text: "+12t" (exact ticks; fractional averages keep 2 decimals) or "+3.00 pts".
 */
function distanceText(
  pos: ExactPosition,
  priceTicks: bigint | number,
  unit: "points" | "ticks",
  tick: LineTick | null,
): string {
  const q = BigInt(Math.abs(pos.netQty));
  const tc = openTickContracts(pos, priceTicks); // Σ ticks over |q| contracts
  if (unit === "ticks" || tick === null) {
    const whole = tc % q === 0n;
    return `${ratioText(tc, q, whole ? 0 : 2)}t`;
  }
  const dec = Math.max(0, Math.min(8, tick.decimals));
  return `${ratioText(tc * BigInt(tick.tickNum), q * BigInt(tick.tickDen), dec)} pts`;
}

/**
 * P&L if an order with `basis` fills at `priceTicks`, in the basis' unit (the drag ghost and the
 * static label use this one function). Cash: integer cents, rounded half away from zero.
 */
export function linePnlAt(
  basis: LinePnlBasis,
  priceTicks: number,
): { text: string; tone: ChartTradeLine["pnlTone"] } {
  const pos: ExactPosition = { netQty: basis.netQty, openCostTicks: BigInt(basis.openCostTicks) };
  const q = BigInt(Math.abs(basis.netQty));
  if (q === 0n) return { text: "", tone: "none" };
  const tc = openTickContracts(pos, priceTicks);
  const t = tone(tc);
  if (basis.privacy) return { text: PRIVACY_MASK, tone: t };
  if (basis.unit === "cash") {
    const cents = roundHalfAway(
      openPnlCents(pos, priceTicks, basis.tickValueCents) * BigInt(basis.closeQty),
      q,
    );
    return { text: formatCents(cents, { signed: true }), tone: tone(cents) };
  }
  return { text: distanceText(pos, priceTicks, basis.unit, basis.tick), tone: t };
}

// ------------------------------------------------------------------------------------ lines

/**
 * Lines for one contract. `positions` / `orders` are already filtered to the accounts in view;
 * orders that are not working are skipped here too (defence in depth); `showAccount` suffixes
 * labels with the account (several accounts shown).
 */
export function chartTradeLines(opts: {
  positions: readonly LinePosition[];
  orders: readonly LineOrder[];
  tickValueCents: number;
  privacy: boolean;
  showAccount: boolean;
  accountLabel?: (id: string) => string;
  /** P&L unit (default cash). */
  unit?: ChartPnlUnit;
  tick?: LineTick | null;
  /**
   * Gate reasons (null = allowed), or per account (a line acts on its own order's account).
   * Omitted: nothing is actionable (read-only lines).
   */
  gates?: LineGates | ((accountId: string) => LineGates);
}): ChartTradeLine[] {
  const unit = opts.unit ?? "cash";
  const tick = opts.tick ?? null;
  const off: LineGates = {
    modify: "Chart trading is off for this chart",
    cancel: "Chart trading is off for this chart",
    protect: "Chart trading is off for this chart",
  };
  const gatesOf = (id: string): LineGates =>
    opts.gates === undefined ? off : typeof opts.gates === "function" ? opts.gates(id) : opts.gates;
  const out: ChartTradeLine[] = [];
  const acct = (id: string): string => (opts.showAccount ? ` · ${opts.accountLabel?.(id) ?? id}` : "");
  const money = (c: bigint | null): string | null =>
    c === null ? null : formatCents(c, { signed: true, privacy: opts.privacy });
  // Points / ticks need no tick value; cash does.
  const canPnl = unit !== "cash" || opts.tickValueCents > 0;
  for (const p of opts.positions) {
    const q = p.position.netQty;
    if (q === 0) continue;
    const mark = p.markTicks ?? null;
    let pnl: string;
    let pnlTone: ChartTradeLine["pnlTone"];
    if (unit === "cash") {
      pnl = money(p.unrealizedCents) ?? "$ --";
      pnlTone = tone(p.unrealizedCents);
    } else if (mark === null) {
      pnl = unit === "ticks" ? "-- t" : "-- pts";
      pnlTone = "none";
    } else {
      pnl = opts.privacy ? PRIVACY_MASK : distanceText(p.position, mark, unit, tick);
      pnlTone = tone(openTickContracts(p.position, mark));
    }
    // What protects it now: TP / SL children on its account, whether an entry's bracket placed
    // them, and other working orders on the closing side (they keep the room a TP / SL needs).
    const live = opts.orders.filter((o) => {
      const st = o.state ?? PB.WORKING;
      return (
        o.accountId === p.accountId &&
        (o.side === PB.SIDE_SELL) === q > 0 &&
        (st in STATUS || ACTIONABLE.has(st)) &&
        o.qty - o.filled > 0
      );
    });
    const child = (role: number): boolean => live.some((o) => o.bracketRole === role);
    const protect = protectSetOf({
      hasTp: child(PB.ROLE_TARGET),
      hasSl: child(PB.ROLE_STOP),
      entryBracket: live.some(
        (o) =>
          (o.bracketRole === PB.ROLE_TARGET || o.bracketRole === PB.ROLE_STOP) &&
          !(o.parentId ?? "").startsWith("PB-"),
      ),
      closing: live.some((o) => o.bracketRole !== PB.ROLE_TARGET && o.bracketRole !== PB.ROLE_STOP),
    });
    const pg = gatesOf(p.accountId);
    const protectWhy = pg.protect === undefined ? pg.modify : pg.protect;
    const set = protectWhy === null ? protect.set : null;
    const closable = pg.close === null;
    const reversible = pg.reverse === null;
    out.push({
      id: `pos:${p.accountId}`,
      ...(p.contextKey === undefined ? {} : { contextKey: p.contextKey }),
      kind: "position",
      priceTicks: Number(p.position.openCostTicks) / Math.abs(q),
      label: `${q > 0 ? "▲ LONG" : "▼ SHORT"}${acct(p.accountId)}`,
      qty: signedQty(q),
      pnl,
      pnlTone,
      side: q > 0 ? "buy" : "sell",
      tooltip: [
        "Position · average price; open P&L at the last price",
        protectWhy ?? protect.why,
        ...(pg.close !== undefined && pg.close !== null ? [`Close: ${pg.close}`] : []),
        ...(pg.reverse !== undefined && pg.reverse !== null && pg.reverse !== pg.close
          ? [`Reverse: ${pg.reverse}`]
          : []),
      ].join(" · "),
      orderId: null,
      accountId: p.accountId,
      dashed: false,
      draggable: false,
      cancellable: false,
      status: null,
      pnlBasis: {
        netQty: q,
        openCostTicks: p.position.openCostTicks.toString(),
        closeQty: Math.abs(q),
        tickValueCents: opts.tickValueCents,
        unit,
        tick,
        privacy: opts.privacy,
      },
      protect: set,
      protectReason: protectWhy ?? protect.why,
      reversible,
      closable,
      account: opts.showAccount ? (opts.accountLabel?.(p.accountId) ?? p.accountId) : null,
    });
  }
  for (const o of opts.orders) {
    const state = o.state ?? PB.WORKING;
    if (!(state in STATUS) && !ACTIONABLE.has(state)) continue; // finished: never a line
    const px = orderPrice(o);
    if (px === null) continue;
    const open = o.qty - o.filled;
    if (open <= 0) continue;
    const sell = o.side === PB.SIDE_SELL;
    const pos = opts.positions.find((p) => p.accountId === o.accountId)?.position;
    // Realised P&L if it fills: only for an order that reduces the account's position.
    const reduces = pos !== undefined && pos.netQty !== 0 && pos.netQty > 0 === sell;
    const basis: LinePnlBasis | null =
      reduces && canPnl
        ? {
            netQty: pos.netQty,
            openCostTicks: pos.openCostTicks.toString(),
            closeQty: Math.min(open, Math.abs(pos.netQty)),
            tickValueCents: opts.tickValueCents,
            unit,
            tick,
            privacy: opts.privacy,
          }
        : null;
    const at = basis ? linePnlAt(basis, Number(px)) : null;
    const kind: ChartTradeLine["kind"] =
      o.bracketRole === PB.ROLE_TARGET ? "target" : o.bracketRole === PB.ROLE_STOP ? "stop" : "order";
    const head =
      kind === "target"
        ? "TP"
        : kind === "stop"
          ? "SL"
          : `${sell ? "SELL" : "BUY"} ${TYPE_SHORT[o.type] ?? ""}`.trim();
    const actionable = ACTIONABLE.has(state);
    const gates = gatesOf(o.accountId);
    const draggable = actionable && DRAGGABLE_TYPES.has(o.type) && gates.modify === null;
    const cancellable = actionable && gates.cancel === null;
    const status = STATUS[state] ?? null;
    const why: string[] = [];
    if (draggable) why.push("Drag to move (sent on release)");
    else if (!actionable) why.push(notActionableWhy(state, status));
    else if (!DRAGGABLE_TYPES.has(o.type))
      why.push(`${TYPE_SHORT[o.type] ?? "This"} orders are moved in the ticket`);
    else if (gates.modify !== null) why.push(gates.modify);
    if (cancellable) why.push("× cancels");
    else if (actionable && gates.cancel !== null && gates.cancel !== gates.modify) why.push(gates.cancel);
    out.push({
      id: `ord:${o.clientOrderId}`,
      ...(o.contextKey === undefined ? {} : { contextKey: o.contextKey }),
      kind,
      priceTicks: Number(px),
      label: `${head}${at !== null ? ` ~ ${at.text}` : ""}${acct(o.accountId)}`,
      qty: signedQty(sell ? -open : open),
      pnl: null,
      pnlTone: at?.tone ?? "none",
      side: sell ? "sell" : "buy",
      tooltip: why.join(" · "),
      orderId: o.clientOrderId,
      accountId: o.accountId,
      dashed: kind !== "order" || o.type !== PB.LIMIT,
      draggable,
      cancellable,
      status,
      pnlBasis: basis,
      protect: null,
      protectReason: null,
      reversible: false,
      closable: false,
      account: null,
    });
  }
  return out;
}

/** The ghost label while a line is dragged: "SL 21,445.00 · −12t from last · −$60.00". */
export function ghostLabel(
  line: Pick<ChartTradeLine, "kind" | "label" | "pnlBasis">,
  toTicks: number,
  priceText: string,
  lastTicks: number | null,
): string {
  const head = line.label.split(" ~ ")[0]?.split(" · ")[0] ?? line.label;
  const parts = [`${head} ${priceText}`];
  if (lastTicks !== null) {
    const d = toTicks - lastTicks;
    parts.push(`${d > 0 ? "+" : d < 0 ? MINUS : ""}${String(Math.abs(d))}t from last`);
  }
  if (line.pnlBasis) parts.push(linePnlAt(line.pnlBasis, toTicks).text);
  return parts.join(" · ");
}
